import { guidanceDayKey, type GuidanceSource } from '@bookora/shared';

/**
 * Every browser-storage key guidance touches, and the only code that touches
 * them. Every access sits in try/catch: a private window, a full quota or a
 * blocked origin must never break a tour.
 *
 *   bookora_guidance_last_offer                 ISO time of the last proactive
 *                                               item in ANY tab (cross-tab budget)
 *   bookora_guidance_core_seen:<userId>         '1' after the core tour, any clinic
 *   bookora_guidance_host_opens:<u>:<c>:<key>   how often an inline host was opened
 *   bookora_guidance_help:<u>:<c>               per-context help-offer backoff
 *                                               { [context]: { shownAt?, declinedAt? } }
 *   bookora_guidance_session (sessionStorage)   { dayKey, autoShown, helpShown } per-tab guard
 *   bookora_guidance_armed (sessionStorage)     the tour waiting for its place (this tab)
 *
 * Nothing here holds clinical data: keys, tour ids, section keys and times.
 */

const LAST_OFFER_KEY = 'bookora_guidance_last_offer';
const SESSION_KEY = 'bookora_guidance_session';
const ARMED_KEY = 'bookora_guidance_armed';

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage unavailable: the server's budget still holds.
  }
}

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

/** When any tab last showed something proactive (the server's clock is authoritative on the next GET). */
export function readLastOffer(): string | null {
  return readLocal(LAST_OFFER_KEY);
}

export function writeLastOffer(now: Date): void {
  writeLocal(LAST_OFFER_KEY, now.toISOString());
}

export function clearLastOffer(): void {
  writeLocal(LAST_OFFER_KEY, null);
}

export function coreSeenKey(userId: string): string {
  return `bookora_guidance_core_seen:${userId}`;
}

export function readCoreSeen(userId: string | null | undefined): boolean {
  return Boolean(userId) && readLocal(coreSeenKey(userId as string)) === '1';
}

export function writeCoreSeen(userId: string | null | undefined, seen: boolean): void {
  if (!userId) return;
  writeLocal(coreSeenKey(userId), seen ? '1' : null);
}

function hostOpensKey(userId: string, clinicId: string, key: string): string {
  return `bookora_guidance_host_opens:${userId}:${clinicId}:${key}`;
}

export function readHostOpens(userId: string | null, clinicId: string | null, key: string): number {
  if (!userId || !clinicId) return 0;
  const n = Number(readLocal(hostOpensKey(userId, clinicId, key)));
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

export function writeHostOpens(
  userId: string | null,
  clinicId: string | null,
  key: string,
  n: number,
): void {
  if (!userId || !clinicId) return;
  writeLocal(hostOpensKey(userId, clinicId, key), String(n));
}

/* ── The per-tab daily guard ────────────────────────────────────────────── */

interface TabGuard {
  dayKey: string;
  autoShown: number;
  /** Help offers (struggle), counted apart so they and first-visit offers do not starve each other. */
  helpShown: number;
}

function readTabGuard(): TabGuard | null {
  try {
    const raw = readSession(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TabGuard>;
    if (typeof parsed.dayKey !== 'string' || typeof parsed.autoShown !== 'number') return null;
    return {
      dayKey: parsed.dayKey,
      autoShown: parsed.autoShown,
      helpShown: typeof parsed.helpShown === 'number' ? parsed.helpShown : 0,
    };
  } catch {
    return null;
  }
}

function writeTabGuard(guard: TabGuard): void {
  writeSession(SESSION_KEY, JSON.stringify(guard));
}

function todayGuard(now: Date): TabGuard {
  const dayKey = guidanceDayKey(now);
  const guard = readTabGuard();
  return guard && guard.dayKey === dayKey ? guard : { dayKey, autoShown: 0, helpShown: 0 };
}

/**
 * At most one auto-offer or welcome per tab per Tehran DAY: a reception tab
 * left open for a week gets one a day, not one a week and not one an hour.
 */
export function tabGuardAllows(now: Date): boolean {
  return todayGuard(now).autoShown < 1;
}

export function markTabAutoShown(now: Date): void {
  const guard = todayGuard(now);
  writeTabGuard({ ...guard, autoShown: guard.autoShown + 1 });
}

/** At most one help offer per tab per Tehran day, whatever the first-visit offers did. */
export function tabHelpAllows(now: Date): boolean {
  return todayGuard(now).helpShown < 1;
}

export function markTabHelpShown(now: Date): void {
  const guard = todayGuard(now);
  writeTabGuard({ ...guard, helpShown: guard.helpShown + 1 });
}

export function clearTabGuard(): void {
  writeSession(SESSION_KEY, null);
}

/** Spend the local halves of the proactive budget after showing something proactive. */
export function noteProactiveShown(now: Date): void {
  writeLastOffer(now);
  markTabAutoShown(now);
}

/** The same for a help offer: the cross-tab gap, and this tab's help guard. */
export function noteHelpShown(now: Date): void {
  writeLastOffer(now);
  markTabHelpShown(now);
}

/* ── Per-context help backoff ───────────────────────────────────────────── */

export interface HelpBackoff {
  shownAt: string | null;
  declinedAt: string | null;
}

function helpKey(userId: string, clinicId: string): string {
  return `bookora_guidance_help:${userId}:${clinicId}`;
}

function readHelpMap(userId: string | null, clinicId: string | null): Record<string, HelpBackoff> {
  if (!userId || !clinicId) return {};
  try {
    const raw = readLocal(helpKey(userId, clinicId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, HelpBackoff>) : {};
  } catch {
    return {};
  }
}

export function readHelpBackoff(
  userId: string | null,
  clinicId: string | null,
  context: string,
): HelpBackoff {
  const entry = readHelpMap(userId, clinicId)[context];
  return {
    shownAt: typeof entry?.shownAt === 'string' ? entry.shownAt : null,
    declinedAt: typeof entry?.declinedAt === 'string' ? entry.declinedAt : null,
  };
}

export function writeHelpBackoff(
  userId: string | null,
  clinicId: string | null,
  context: string,
  patch: Partial<HelpBackoff>,
): void {
  if (!userId || !clinicId) return;
  const map = readHelpMap(userId, clinicId);
  map[context] = { ...readHelpBackoff(userId, clinicId, context), ...patch };
  writeLocal(helpKey(userId, clinicId), JSON.stringify(map));
}

/* ── The armed tour (this tab) ──────────────────────────────────────────── */

export interface StoredArmedTour {
  clinicId: string;
  key: string;
  source: GuidanceSource;
  startAtStepId?: string;
  until: number;
}

export function readArmed(clinicId: string | null, now = Date.now()): StoredArmedTour | null {
  if (!clinicId) return null;
  try {
    const raw = readSession(ARMED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredArmedTour>;
    if (
      parsed.clinicId !== clinicId ||
      typeof parsed.key !== 'string' ||
      typeof parsed.source !== 'string' ||
      typeof parsed.until !== 'number' ||
      parsed.until <= now
    ) {
      return null;
    }
    return {
      clinicId,
      key: parsed.key,
      source: parsed.source,
      startAtStepId: typeof parsed.startAtStepId === 'string' ? parsed.startAtStepId : undefined,
      until: parsed.until,
    };
  } catch {
    return null;
  }
}

export function writeArmed(armed: StoredArmedTour | null): void {
  writeSession(ARMED_KEY, armed ? JSON.stringify(armed) : null);
}
